import { useState } from "react";
import { X, Phone, Calendar, FileText } from "lucide-react";
import { callLogAPI } from "../api/client";
import eventBus from "../utils/eventBus"; // Task 34
import toast from "../utils/toast"; // Task 34

function CallModal({ isOpen, onClose, candidate, onSuccess }) {
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");  // Task 12
  const [followUpTime, setFollowUpTime] = useState("");  // Task 12
  const [nextAction, setNextAction] = useState("");  // Task 12
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen || !candidate) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Task 12: Validate outcome is required
    if (!outcome) {
      setError("Please select a call outcome");
      return;
    }
    
    // Task 14: Validate next action is required
    if (!nextAction || nextAction.trim() === "") {
      setError("Please enter Next Action (or type 'NA' if no follow-up required)");
      return;
    }
    
    setSubmitting(true);

    try {
      // Task 12: Combine follow-up date and time if both provided
      let followUpDatetime = null;
      if (followUpDate && followUpTime) {
        followUpDatetime = `${followUpDate}T${followUpTime}:00`;
      } else if (followUpDate) {
        followUpDatetime = `${followUpDate}T09:00:00`;  // Default to 9 AM if time not specified
      }
      
      const response = await callLogAPI.create({
        candidate_id: candidate.id,
        outcome: outcome,
        notes: notes || null,
        follow_up_datetime: followUpDatetime,  // Task 12
        next_action: nextAction || null,  // Task 12
      });
      
      // Task 34: Emit analytics event
      await eventBus.emit('log_added', {
        candidate_id: candidate.id,
        source: 'CallModal',
        metadata: {
          outcome,
          has_follow_up: !!followUpDatetime,
          has_notes: !!notes,
          candidate_name: candidate.name
        }
      });
      
      // Task 34: Show success toast
      toast.success('Call log added successfully');
      onSuccess?.(response.data);
      
      // Reset form
      setOutcome("");
      setNotes("");
      setFollowUpDate("");  // Task 12
      setFollowUpTime("");  // Task 12
      setNextAction("");  // Task 12
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save call log");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-navy/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-navy-light border border-teal/30 rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-teal/20">
          <div>
            <h3 className="text-xl font-heading font-semibold text-gold flex items-center">
              <Phone className="mr-2 text-teal" size={24} />
              Add Call Log
            </h3>
            <p className="text-sm text-textLight/70 mt-1">{candidate.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-textLight/60 hover:text-gold p-1 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-gold/20 border border-gold/50 rounded text-gold text-sm">
              {error}
            </div>
          )}

          {/* Candidate Info (Auto-filled, Task 12) */}
          <div className="mb-4 p-3 bg-navy/50 border border-teal/20 rounded-lg">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-textLight/60">Role:</span>
                <span className="ml-2 text-textLight font-medium">{candidate.role || 'N/A'}</span>
              </div>
              <div>
                <span className="text-textLight/60">Phone:</span>
                <span className="ml-2 text-textLight font-medium">{candidate.phone || 'N/A'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-textLight/60">Email:</span>
                <span className="ml-2 text-textLight font-medium">{candidate.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Outcome Field (Required, Task 12) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-textLight mb-2">
              Outcome <span className="text-red-400">*</span>
            </label>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              required
              className="w-full px-3 py-2 bg-navy border border-teal/30 rounded-lg focus:ring-2 focus:ring-teal focus:border-teal text-textLight"
            >
              <option value="" className="bg-navy text-textLight">Select outcome...</option>
              <option value="Shortlisted" className="bg-navy text-textLight">Shortlisted</option>
              <option value="Rejected" className="bg-navy text-textLight">Rejected</option>
              <option value="Not Interested" className="bg-navy text-textLight">Not Interested</option>
              <option value="Wrong Number" className="bg-navy text-textLight">Wrong Number</option>
              <option value="Follow-up Scheduled" className="bg-navy text-textLight">Follow-up Scheduled</option>
              <option value="Callback Requested" className="bg-navy text-textLight">Callback Requested</option>
              <option value="No Response" className="bg-navy text-textLight">No Response</option>
            </select>
          </div>

          {/* Follow-up Date & Time (Task 12) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-textLight mb-2 flex items-center">
              <Calendar size={16} className="mr-1 text-teal" />
              Follow-up Date & Time (Optional)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="px-3 py-2 bg-navy border border-teal/30 rounded-lg focus:ring-2 focus:ring-teal focus:border-teal text-textLight"
              />
              <input
                type="time"
                value={followUpTime}
                onChange={(e) => setFollowUpTime(e.target.value)}
                className="px-3 py-2 bg-navy border border-teal/30 rounded-lg focus:ring-2 focus:ring-teal focus:border-teal text-textLight"
              />
            </div>
          </div>

          {/* Next Action (Task 14: Now Mandatory) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-textLight mb-2 flex items-center">
              <FileText size={16} className="mr-1 text-teal" />
              Next Action <span className="text-red-400 ml-1">*</span>
            </label>
            <p className="text-xs text-textLight/60 mb-2 italic">
              Enter 'NA' if no follow-up or next step required.
            </p>
            <textarea
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              rows={2}
              placeholder="Describe the next action to take (or type 'NA')..."
              required
              className="w-full px-3 py-2 bg-navy border border-teal/30 rounded-lg focus:ring-2 focus:ring-teal focus:border-teal resize-none text-textLight placeholder-textLight/50"
            />
          </div>

          {/* Notes Field */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-textLight mb-2">
              Call Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add call notes..."
              className="w-full px-3 py-2 bg-navy border border-teal/30 rounded-lg focus:ring-2 focus:ring-teal focus:border-teal resize-none text-textLight placeholder-textLight/50"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-teal/30 rounded-lg text-textLight hover:bg-navy font-medium transition-all"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-teal text-white rounded-lg hover:bg-teal-dark font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-teal/30"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Call Log"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CallModal;
